import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IdObject } from 'src/app/core/id-object';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable, of } from 'rxjs';
import { Account, AccountSearchData } from '../account/account';
import { CompanyAccount, CompanyAccountDraft, CompanyAccountSearchData } from './company';

@Injectable({
  providedIn: 'root',
})
export class CompanyAccountService {
  private static listUrl = (apiUrl: string): string => `${apiUrl}companyAccount`;
  private static singleUrl = (apiUrl: string, id: string): string => `${apiUrl}companyAccount/${id}`;
  private static searchUrl = (apiUrl: string): string => `${apiUrl}companyAccount/search`;

  constructor(private httpClient: HttpClient, private logger: NGXLogger) {
  }

  public getList(): Observable<CompanyAccount[]> {
    const {apiUrl} = environment;
    const url = CompanyAccountService.listUrl(apiUrl);

    return this.httpClient
      .get<CompanyAccount[]>(url)
      .pipe(catchError(this.handleError<CompanyAccount[]>('getList', [])));
  }

  public getSingle(id: string): Observable<CompanyAccount> {
    const {apiUrl} = environment;
    const url = CompanyAccountService.singleUrl(apiUrl, id);

    return this.httpClient
      .get<CompanyAccount>(url)
      .pipe(catchError(this.handleError<CompanyAccount>('getSingle')));
  }

  public search(userSearchData: CompanyAccountSearchData): Observable<CompanyAccount[]> {
    const {apiUrl} = environment;
    const url = CompanyAccountService.searchUrl(apiUrl);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient
      .post<CompanyAccount[]>(url, userSearchData, {headers})
      .pipe(catchError(this.handleError<CompanyAccount[]>('search', [])));
  }

  public create(userDraft: CompanyAccountDraft): Observable<string> {
    const {apiUrl} = environment;
    const url = CompanyAccountService.listUrl(apiUrl);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.post<IdObject>(url, userDraft, {headers}).pipe(
      map((idObject: IdObject) => idObject.id),
      catchError(this.handleError<string>('create')),
    );
  }

  public patch(id: string, userDraft: CompanyAccountDraft): Observable<boolean> {
    const {apiUrl} = environment;
    const url = CompanyAccountService.singleUrl(apiUrl, id);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.patch<void>(url, userDraft, {headers}).pipe(
      map(() => true),
      catchError(this.handleError<boolean>('patch', false)),
    );
  }

  public remove(id: string): Observable<boolean> {
    const {apiUrl} = environment;
    const url = CompanyAccountService.singleUrl(apiUrl, id);

    return this.httpClient.delete<void>(url).pipe(
      map(() => true),
      catchError(this.handleError('remove', false)),
    );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(
    operation = 'operation',
    result?: T,
  ): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      this.logger.error(`${CompanyAccountService.name}->${operation} failed: `, error);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
